import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Config
import { uiConfig } from '../../../../../../../../../../common/conf/codes';

// Components
import { DualList } from '../../../../../../../../../../components/DualList';
import { Button } from '../../../../../../../../../../components/Button';
import { Dialog } from '../../../../../../../../../../components/Dialog';
import { DialogHeader } from '../../../../../../../../../../components/DialogHeader';
import { DialogBody } from '../../../../../../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../../../../../../components/DialogFooter';
import { SimpleTable } from '../../../../../../../../../../components/SimpleTable';

// Enums
import { dtoState } from '../../../../../../../../../../common/enums/dtoState';

const RowWrapper = styled.div`
    flex: 1;
`;

const reduceListItems = (listItems = []) =>
    listItems.reduce(
        (acc, listItem) => ({
            ...acc,
            [listItem?.cdsCodeDTO?.codeId]: listItem.dtoState !== dtoState.Deleted
        }),
        {}
    );

export const CodeSelectionModal = ({ closeDialog, onApply, allItems, partId, currentListItems = [] }) => {
    const [selected, setSelected] = React.useState({});
    const selectCode = React.useCallback(
        (id) => {
            if (!Object.hasOwn(selected, id)) {
                setSelected({
                    ...selected,
                    [id]: true
                });
            } else {
                setSelected({
                    ...selected,
                    [id]: !selected[id]
                });
            }
        },
        [selected, setSelected]
    );
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const selectedCount = React.useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);
    const wrapCode = React.useCallback(
        ({ code }) => ({
            '@class': 'org.cdsframework.dto.CriteriaPredicatePartConceptDTO',
            dtoState: dtoState.New,
            partId,
            // itemType: listTypes.AD_HOC_CONCEPT,
            // lastModDatetime: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxxx"),
            // lastModId: code.createId,
            // listId: listId,
            cdsCodeDTO: code,
            uuid: uuid4()
        }),
        [partId]
    );

    React.useEffect(() => {
        setSelected(reduceListItems(currentListItems));
    }, [currentListItems, setSelected]);

    const prepareSelection = React.useCallback(() => {
        const currentlySelected = [...currentListItems];
        const cdsCodeDTOUpdated = Object.entries(selected).reduce((acc, [currKey, currVal]) => {
            const found = currentlySelected.find((item) => item?.cdsCodeDTO?.codeId === currKey);

            if (found) {
                return !currVal && found.dtoState === dtoState.New
                    ? acc
                    : [
                          ...acc,
                          {
                              ...found,
                              dtoState: currVal
                                  ? found.dtoState === dtoState.New
                                      ? dtoState.New
                                      : dtoState.Unset
                                  : dtoState.Deleted
                          }
                      ];
            } else if (currVal) {
                const code = allItems.find((code) => currKey === code[uiConfig.primaryKey]);
                return [
                    ...acc,
                    wrapCode({
                        code
                    })
                ];
            } else {
                return acc;
            }
        }, []);

        onApply(cdsCodeDTOUpdated);
        closeDialog();
    }, [selected, allItems, currentListItems, onApply]);

    const renderRowContents = (item, isSelected) => {
        return (
            <RowWrapper>
                <SimpleTable
                    id={`code-select-row__${item.uuid}`}
                    columns={[
                        {
                            Header: ``,
                            accessor: `label`,
                            disableSortBy: true,
                            filterable: false,
                            size: 50,
                            maxWidth: 50,
                            Cell: ({ value }) => <span style={{ fontWeight: 600 }}>{value}</span>
                        },
                        {
                            Header: ``,
                            accessor: `content`,
                            disableSortBy: true,
                            filterable: false
                        }
                    ]}
                    data={[
                        {
                            label: 'Code',
                            content: item.code,
                            DataTableActions: null
                        },
                        {
                            label: 'Code System OID',
                            content: item.codeSystem,
                            DataTableActions: null
                        },
                        {
                            label: 'Code System Name',
                            content: item.codeSystemName,
                            DataTableActions: null
                        },
                        {
                            label: 'Name',
                            content: item.displayName,
                            DataTableActions: null
                        }
                    ]}
                />
            </RowWrapper>
        );
    };

    return (
        <Dialog close={CloseDialog} size="full" visible id="criteria-manager-code-table-modal">
            <DialogHeader close={CloseDialog}>Code Selection Table</DialogHeader>
            <DialogBody style={{ paddingBottom: 0 }}>
                <DualList
                    allItems={allItems}
                    selected={selected}
                    onSelect={(item) => selectCode(item.codeId)}
                    isLoading={false}
                    renderRowContents={renderRowContents}
                    primaryKey={'codeId'}
                    contentType={'codes'}
                    searchableProperties={['code', 'displayName']}
                />
            </DialogBody>
            <DialogFooter>
                <Button text appearance="primary" onClick={prepareSelection} loading={false}>
                    Apply
                </Button>
                <Button text appearance="secondary" onClick={CloseDialog}>
                    Cancel
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default CodeSelectionModal;

CodeSelectionModal.propTypes = {
    partId: PropTypes.string,
    closeDialog: PropTypes.func,
    onApply: PropTypes.func,
    allItems: PropTypes.arrayOf(PropTypes.object),
    currentListItems: PropTypes.arrayOf(PropTypes.object)
};
